import { useMemo } from 'react';

import { Box, Image, PressableBox, Text } from '@opendoor/bricks-next';
import {
  buildFloorPlanSrc,
  buildGalleryPhotos,
  GalleryPhoto,
} from 'components/marketplace/helpers/galleryPhotos';
import { useTrackProperty } from 'components/marketplace/helpers/useTrackProperty';
import { Map } from 'components/marketplace/property/Map';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import { generatePresetPictureSet, PresetPictureSet } from 'components/shared/performance/pictures';

import styles from './GalleryPhotos.module.css';

const GALLERY_SOURCE_SET_CONFIG: PresetPictureSet = [
  {
    minWidth: 768,
    maxWidth: 1201,
    preset: '3:2-md',
    presetScale: '2/3',
  },
  {
    minWidth: 1201,
    maxWidth: 1400,
    preset: '3:2-lg',
    presetScale: '3/4',
  },
  {
    minWidth: 1400,
    maxWidth: null,
    preset: '3:2-xl',
    presetScale: '1',
  },
];

interface GalleryPhotoProps {
  photo: GalleryPhoto;
  idx: number;
  totalPhotos: number;
  address: string;
  toggleLightbox: () => void;
  setSelectedPhotoIndex: (idx: number) => void;
  isLastPhoto: boolean;
}

const GalleryPhotoTile = ({
  photo,
  idx,
  totalPhotos,
  address,
  toggleLightbox,
  setSelectedPhotoIndex,
  isLastPhoto,
}: GalleryPhotoProps) => {
  const { trackProperty } = useTrackProperty();
  const handleClick = () => {
    trackProperty('property-images', 'marketplace-pdp-lightbox');
    toggleLightbox();
    setSelectedPhotoIndex(idx);
  };

  const floorPlanLabel = photo.isFloorPlan ? photo.label?.trim() || 'Floor plan' : null;
  const alt = floorPlanLabel
    ? `${floorPlanLabel}${address ? ` of ${address}` : ''}`
    : `Photo ${idx + 1} of ${totalPhotos}${address ? ` of ${address}` : ''}`;

  return (
    <PressableBox
      analyticsName={
        photo.isFloorPlan
          ? 'cosmos-marketplace-property-desktop-gallery-floor-plan'
          : 'cosmos-marketplace-property-desktop-gallery-photo'
      }
      aria-label={alt}
      key={`${idx}-photo`}
      mb={isLastPhoto ? '$0' : '$3'}
      py={0}
      px={0}
      onPress={handleClick}
      width="100%"
      position="relative"
    >
      {photo.isFloorPlan ? (
        // Floor plans are often portrait or non-3:2, so the listing photo
        // preset (3:2-md/lg/xl) crops their edges off. Render them
        // uncropped, object-fit: contain, inside a 3:2 tile on a neutral
        // background so the full plan shows with letterboxing.
        <Box
          width="100%"
          aspectRatio={3 / 2}
          backgroundColor="$backgroundSecondary"
          overflow="hidden"
        >
          <Image
            alt={alt}
            source={{ uri: buildFloorPlanSrc(photo.url) }}
            resizeMode="contain"
            width="100%"
            height="100%"
          />
        </Box>
      ) : (
        generatePresetPictureSet({
          debugImageSizes: false,
          photoUrl: photo.url,
          sizes: GALLERY_SOURCE_SET_CONFIG,
          lazy: idx !== 0,
          alt,
          preload: idx === 0,
          fetchPriority: idx === 0 ? 'high' : undefined,
        })
      )}
      {floorPlanLabel && (
        <Box
          position="absolute"
          left={12}
          bottom={12}
          px="$6x"
          py="$2x"
          borderRadius="$4x"
          backgroundColor="$backgroundInversePrimary"
        >
          <Text tag="span" typography="$labelXsmall" color="$contentInversePrimary">
            {floorPlanLabel}
          </Text>
        </Box>
      )}
    </PressableBox>
  );
};

interface GalleryPhotosProps {
  property: PropertyDetailsType;
  toggleLightbox: () => void;
  handleSetSelectedPhotoIndex: (idx: number) => void;
}
export const GalleryPhotos = ({
  property,
  toggleLightbox,
  handleSetSelectedPhotoIndex,
}: GalleryPhotosProps) => {
  const galleryPhotos = useMemo(() => buildGalleryPhotos(property), [property]);
  const totalPhotos = galleryPhotos.length;
  const address = [property?.street, property?.city, property?.state].filter(Boolean).join(', ');

  const Gallery = useMemo(() => {
    const photos = galleryPhotos.map((photo, idx) => {
      const galleryPhoto = (
        <GalleryPhotoTile
          key={idx}
          idx={idx}
          totalPhotos={totalPhotos}
          address={address}
          photo={photo}
          toggleLightbox={toggleLightbox}
          setSelectedPhotoIndex={handleSetSelectedPhotoIndex}
          isLastPhoto={idx === totalPhotos - 1}
        />
      );

      if (idx == 1) {
        return (
          <Box
            key="split-photo-map-view"
            flexDirection="row"
            mb="$3"
            justifyContent="space-between"
            width="100%"
          >
            <Box width="49.75%" height="100%" aspectRatio={16 / 9} overflow="hidden">
              {galleryPhoto}
            </Box>
            <Box width="49.75%" aspectRatio={16 / 9} backgroundColor="$backgroundSecondary">
              <Map
                key="property-location"
                latitude={property.latitude}
                longitude={property.longitude}
              />
            </Box>
          </Box>
        );
      }

      return galleryPhoto;
    });

    return photos;
  }, [
    galleryPhotos,
    totalPhotos,
    address,
    property.latitude,
    property.longitude,
    toggleLightbox,
    handleSetSelectedPhotoIndex,
  ]);
  return (
    <Box
      height="100%"
      $smallerThanMD={{ display: 'none', overflow: 'hidden' }}
      $largerThanMD={{ flexBasis: '50%', overflow: 'scroll' }}
      $smallerThanLG={{ flexBasis: '50%', overflow: 'scroll' }}
      $largerThanLG={{ flex: 1 }}
      alignItems="center"
      width="100%"
      className={styles.hideScrollbar}
    >
      {Gallery}
    </Box>
  );
};
